import React, { ReactNode, useMemo } from 'react';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { AppTheme, SxProps } from '@cp/ds';
import { Button } from '@cp/ds/src/components/button';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { AdvantageList, AdvantageTokens } from '@cp/entities/advantage';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { CareerTag } from '@cp/entities/career';
import { getResumeGeoPoint } from '@cp/entities/resume';
import { Typography } from '@cp/ds/src/components/typography';
import { ClientLastSeenText, days190strategy } from '@cp/entities/clientOnlineStatus';
import { CLIENT_CATALOG_REVIEW_PAGINATION_SIZE_MAX } from '@common/const/catalog';
import { FeedbacksList } from '@cp/entities/feedback';
import { Link } from '@cp/ds/src/components/link';
import { Schema } from '@cp/ds/src/components/schema';
import { CAREER_IDS_WITH_ENABLED_ADDITIONAL } from '@common/const/catalog/item';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useGetPromocodes } from '@cp/entities/promocode';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Resume as ResumeEntity } from 'src/entities/resume';
import { CatalogItemMap } from 'src/entities/catalog';
import { useCanInvite, useCatalogItemAdvantages, useInviteSpecialist, useProfilePageSchema } from '../../model';
import { ResumeFavorite } from './favorite';

export interface ResumeProps {
  showExtraAgeLabel?: boolean;
  showExperienceLabel?: boolean;
  showOnlineStatus?: boolean;
  showCareerLabel?: boolean;
  data: CatalogItemViewModel | undefined;
  isLoading?: boolean;
  sx?: SxProps;
}

export const Resume: React.FC<ResumeProps> = (props) => {
  const isMobile = useIsMobile();
  const inviteSpecialist = useInviteSpecialist(props.data);
  const isAvailable = useCanInvite(props.data);
  const isSmallScreen = useMediaQuery((theme: AppTheme) => theme.breakpoints.down('lg'));
  const { data: feedbacksData } = coreCatalogItemApi.endpoints.getFeedbacks.useQuery(
    { id: props.data?.id as number, pagination: CLIENT_CATALOG_REVIEW_PAGINATION_SIZE_MAX },
    { skip: !props.data?.id },
  );
  const jsonSchema = useProfilePageSchema(props.data);
  const loggedIn = useIsLoggedIn();
  const promo = useGetPromocodes();
  const clientWithPromo = !loggedIn || promo.length > 0;

  const advantageList = useCatalogItemAdvantages(props.data);
  const advantages: ReactNode = useMemo(() => {
    const sx = { gap: 1, display: 'flex', flexWrap: 'wrap', [AdvantageTokens.colorBackground]: 'white' };
    const advantageProps = { bordered: true };
    return <AdvantageList advantageProps={advantageProps} advantages={advantageList} sx={sx} />;
  }, [advantageList]);

  const careerLabel = useMemo(
    () => (props.data && props.showCareerLabel ? <CareerTag careerId={props.data.careerId} /> : null),
    [props.data, props.showCareerLabel],
  );

  const map = useMemo(() => props.data && <CatalogItemMap location={getResumeGeoPoint(props.data)} sx={{ my: 1 }} />, [props.data]);
  const onlineStatus = useMemo(() => {
    return (
      props.showOnlineStatus &&
      props.data?.clientProfile && (
        <Typography variant="small text bold">
          <ClientLastSeenText profile={props.data?.clientProfile} strategy={days190strategy} />
        </Typography>
      )
    );
  }, [props.data?.clientProfile, props.showOnlineStatus]);

  const favoriteButton = isSmallScreen && props.data?.clientProfile && (
    <ResumeFavorite clientId={props.data?.clientProfile.id} itemSx={{ width: '36px', height: '36px' }} />
  );

  return (
    <ResumeEntity
      favoriteButton={favoriteButton}
      feedbacks={<FeedbacksList {...feedbacksData} />}
      secondaryActionButton={
        <Link color="pink" onClick={inviteSpecialist}>
          Написать сообщение
        </Link>
      }
      showAdditional={CAREER_IDS_WITH_ENABLED_ADDITIONAL.includes(props.data?.careerId || 0)}
      {...props}
      advantages={advantages}
      careerLabel={careerLabel}
      map={map}
      onlineStatusLabel={onlineStatus}
    >
      <Schema schema={jsonSchema} />
      {isSmallScreen && (
        <Button color="gray" disabled={!isAvailable} onClick={inviteSpecialist} size="normal" variant="primary">
          Связаться {clientWithPromo ? 'бесплатно' : ''}
        </Button>
      )}
    </ResumeEntity>
  );
};
Resume.displayName = 'widget/Resume';
